import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RevenueCombined } from '../../../types'
import CellPrice from '@/components/GokuDataGrid/CellPrice'
import TrendingUpIcons from '@/icons/TrendingUpIcons'
import { Typogaphy } from 'vnetwork-ui'

interface MonthlyStatisticsProps {
    revenueData: RevenueCombined[]
    selectedProducts: Set<string>
    filters?: { gte?: string; lte?: string }
}

interface MonthlyRevenue {
    [month: string]: number
}

const MonthlyStatistics: React.FC<MonthlyStatisticsProps> = ({
    revenueData,
    selectedProducts,
    filters,
}) => {
    const { t } = useTranslation()

    // Calculate monthly revenue for selected products
    const { monthlyRevenue, highestMonth } = useMemo(() => {
        const revenue: MonthlyRevenue = {}

        // Filter by selected products only
        const filteredData = revenueData.filter((item) =>
            selectedProducts.has(item.product_group_name)
        )

        filteredData.forEach((item) => {
            const month = item.revenue_month
            if (!revenue[month]) {
                revenue[month] = 0
            }
            revenue[month] += item.revenue_amount || item.allocated_amount || 0
        })

        // Find highest revenue month
        let maxRevenue = 0
        let highestMonth = ''
        Object.entries(revenue).forEach(([month, amount]) => {
            if (amount > maxRevenue) {
                maxRevenue = amount
                highestMonth = month
            }
        })

        return { monthlyRevenue: revenue, highestMonth }
    }, [revenueData, selectedProducts])

    // Extract year from filters
    const year = useMemo(() => {
        if (filters?.gte) {
            return filters.gte.split('-')[0]
        }
        return new Date().getFullYear().toString()
    }, [filters])

    // Generate months for each quarter
    const getMonthKey = (monthNum: number) => {
        return `${year}-${String(monthNum).padStart(2, '0')}`
    }

    const quarters = [
        {
            label: t('quarter_1'),
            months: [1, 2, 3],
        },
        {
            label: t('quarter_2'),
            months: [4, 5, 6],
        },
        {
            label: t('quarter_3'),
            months: [7, 8, 9],
        },
        {
            label: t('quarter_4'),
            months: [10, 11, 12],
        },
    ]

    return (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 shadow-sm">
            <Typogaphy className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-blue-600 rounded"></span>
                {t('selected_service_revenue_by_month')}
            </Typogaphy>

            {/* Quarter I & II */}
            <div className="mb-4">
                <div className="grid grid-cols-2 gap-6">
                    {quarters.slice(0, 2).map((quarter) => (
                        <div key={quarter.label}>
                            <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 mb-3 rounded-md shadow-md">
                                <span className="font-bold text-sm">
                                    {t(quarter?.label)}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {quarter.months.map((month) => {
                                    const monthKey = getMonthKey(month)
                                    const revenue =
                                        monthlyRevenue[monthKey] || 0
                                    const isHighest =
                                        monthKey === highestMonth && revenue > 0

                                    return (
                                        <div
                                            key={month}
                                            className={`relative rounded-lg p-3 transition-all duration-200 ${
                                                isHighest
                                                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg scale-105 border-2 border-green-400'
                                                    : 'bg-white border border-gray-200 hover:border-blue-300 hover:shadow-md'
                                            }`}
                                        >
                                            {isHighest && (
                                                <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 rounded-full p-1 shadow-md">
                                                    <TrendingUpIcons className="w-4 h-4" />
                                                </div>
                                            )}
                                            <div
                                                className={`text-xs font-semibold text-center mb-2 py-1 rounded ${
                                                    isHighest
                                                        ? 'bg-white/30 text-white'
                                                        : 'bg-blue-50 text-blue-700'
                                                }`}
                                            >
                                                {t('month')} {month}
                                            </div>
                                            <div
                                                className={`text-sm font-bold text-center ${
                                                    isHighest
                                                        ? 'text-white'
                                                        : 'text-gray-800'
                                                }`}
                                            >
                                                <CellPrice
                                                    price={revenue}
                                                    className={
                                                        isHighest
                                                            ? 'text-white'
                                                            : ''
                                                    }
                                                />
                                            </div>
                                            {isHighest && (
                                                <div className="text-xs text-center mt-1 text-white font-medium">
                                                    🏆 {t('highest')}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quarter III & IV */}
            <div>
                <div className="grid grid-cols-2 gap-6">
                    {quarters.slice(2, 4).map((quarter) => (
                        <div key={quarter.label}>
                            <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 mb-3 rounded-md shadow-md">
                                <span className="font-bold text-sm">
                                    {t(quarter?.label)}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {quarter.months.map((month) => {
                                    const monthKey = getMonthKey(month)
                                    const revenue =
                                        monthlyRevenue[monthKey] || 0
                                    const isHighest =
                                        monthKey === highestMonth && revenue > 0

                                    return (
                                        <div
                                            key={month}
                                            className={`relative rounded-lg p-3 transition-all duration-200 ${
                                                isHighest
                                                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg scale-105 border-2 border-green-400'
                                                    : 'bg-white border border-gray-200 hover:border-blue-300 hover:shadow-md'
                                            }`}
                                        >
                                            {isHighest && (
                                                <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 rounded-full p-1 shadow-md">
                                                    <TrendingUpIcons className="w-4 h-4" />
                                                </div>
                                            )}
                                            <div
                                                className={`text-xs font-semibold text-center mb-2 py-1 rounded ${
                                                    isHighest
                                                        ? 'bg-white/30 text-white'
                                                        : 'bg-blue-50 text-blue-700'
                                                }`}
                                            >
                                                {t('month')} {month}
                                            </div>
                                            <div
                                                className={`text-sm font-bold text-center ${
                                                    isHighest
                                                        ? 'text-white'
                                                        : 'text-gray-800'
                                                }`}
                                            >
                                                <CellPrice
                                                    price={revenue}
                                                    className={
                                                        isHighest
                                                            ? 'text-white'
                                                            : ''
                                                    }
                                                />
                                            </div>
                                            {isHighest && (
                                                <div className="text-xs text-center mt-1 text-white font-medium">
                                                    🏆 {t('highest')}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default MonthlyStatistics
